import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { safeRetrieveLaunchParams } from "features/telegram/utils";

/** Where this session runs: the browser, the native app's WebView, or the
    Telegram Mini App. Registered on every event (`surface`). Read when used:
    the Mini App check resolves after startup. */
export type AnalyticsSurface = "web" | "native_app" | "telegram_mini_app";
export const getAnalyticsSurface = (): AnalyticsSurface =>
  IS_TELEGRAM_WEB_APP
    ? "telegram_mini_app"
    : window.isReactNativeWebView
    ? "native_app"
    : "web";

/** In the Mini App, which Telegram client runs it (`ios`, `android`,
    `tdesktop`, `macos`, `weba`, …) and its Bot API version; registered on
    every event next to `surface`. */
export const getTelegramClient = (): Record<string, string> => {
  if (!IS_TELEGRAM_WEB_APP) return {};
  const launchParams = safeRetrieveLaunchParams();
  return {
    ...(launchParams?.tgWebAppPlatform
      ? { telegram_platform: String(launchParams.tgWebAppPlatform) }
      : {}),
    ...(launchParams?.tgWebAppVersion
      ? { telegram_version: String(launchParams.tgWebAppVersion) }
      : {}),
  };
};

/** The route a user takes through onboarding, set by their own choices. */
export type OnboardingPath =
  | "demo_prepared"
  | "demo_browse"
  | "demo_build_own"
  | "live_connect"
  | "live_existing";

export interface OnboardingJourney {
  attempt_id: string;
  started_at: number;
  trading_method?: string;
  trading_mode?: "demo" | "live";
  onboarding_path?: OnboardingPath;
  last_step?: string;
}

const STORAGE_KEY = "cornix_onboarding_journey";
// Storage can be unavailable (private mode, some WebViews): keep a copy here.
let memoryJourney: OnboardingJourney | undefined;

const read = (): OnboardingJourney | undefined => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as OnboardingJourney;
  } catch {
    return memoryJourney;
  }
  return memoryJourney;
};

const write = (journey: OnboardingJourney | undefined): void => {
  memoryJourney = journey;
  try {
    if (journey) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(journey));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
};

const newAttemptId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
};

/** The current attempt, started on first use. One attempt lasts until the
    user completes or skips onboarding; reloads and redirects keep it. */
export const getOnboardingJourney = (): OnboardingJourney => {
  const journey = read();
  if (journey) return journey;
  const started = { attempt_id: newAttemptId(), started_at: Date.now() };
  write(started);
  return started;
};

export const updateOnboardingJourney = (
  patch: Partial<OnboardingJourney>
): OnboardingJourney => {
  const journey = { ...getOnboardingJourney(), ...patch };
  write(journey);
  return journey;
};

export const endOnboardingJourney = (): void => write(undefined);

/** The attempt's context, for every onboarding event (none outside one). */
export const getOnboardingJourneyContext = (): Record<string, unknown> => {
  const journey = read();
  if (!journey) return {};
  return {
    onboarding_attempt_id: journey.attempt_id,
    trading_method: journey.trading_method,
    trading_mode: journey.trading_mode,
    onboarding_path: journey.onboarding_path,
    seconds_in_onboarding: Math.max(
      0,
      Math.round((Date.now() - journey.started_at) / 1000)
    ),
  };
};

/** Shared events that are part of the funnel when they happen inside
    onboarding (its routes), without the "Onboarding - " prefix. */
const ONBOARDING_LEGACY_EVENTS = new Set([
  "Connect External Account View",
  "Connect to External Account Clicked",
  "Connect Exchange Try Again Clicked",
  "Telegram UUID Sent",
  "Bot - Create / Save Changes Clicked",
]);

export const isOnboardingEvent = (name: string): boolean =>
  name.startsWith("Onboarding - ") ||
  (ONBOARDING_LEGACY_EVENTS.has(name) &&
    window.location.pathname.startsWith("/register/"));
